import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DOM_EVENT } from '@/shared/constants/keyboard'
import { useCalendar } from '../calendar-panel/use-calendar'

interface UseDatePickerFieldProps {
  value: Date | undefined
  disabled: boolean
  withActions: boolean
  error?: boolean | string
  helpText?: string
  placeholder?: string
  onChange: (date: Date) => void
  onBlur?: () => void
}

export function useDatePickerField({
  value,
  disabled,
  withActions,
  error,
  helpText,
  placeholder,
  onChange,
  onBlur,
}: UseDatePickerFieldProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [stagedDate, setStagedDate] = useState<Date | undefined>(undefined)
  const ref = useRef<HTMLDivElement>(null)
  const { viewDate, prevMonth, nextMonth, goToDate, days } = useCalendar(
    value ?? undefined,
  )

  const hasError = !!error
  const errorMessage = typeof error === 'string' ? error : undefined
  const displayedHelp = errorMessage ?? helpText
  const displayedDate = isOpen && withActions ? stagedDate : value
  const resolvedPlaceholder = placeholder ?? t('calendar.placeholder')

  function open() {
    if (disabled) return
    if (value) goToDate(value)
    setStagedDate(value)
    setIsOpen(true)
  }

  function close() {
    setIsOpen(false)
    setStagedDate(undefined)
    onBlur?.()
  }

  function toggle() {
    if (isOpen) close()
    else open()
  }

  function handleDayClick(date: Date) {
    if (withActions) {
      setStagedDate(date)
      if (date) goToDate(date)
    } else {
      onChange(date)
      close()
    }
  }

  function handleConfirm() {
    if (stagedDate) {
      onChange(stagedDate)
    }
    close()
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
        setStagedDate(undefined)
      }
    }
    document.addEventListener(DOM_EVENT.MOUSEDOWN, handleClickOutside)
    return () =>
      document.removeEventListener(DOM_EVENT.MOUSEDOWN, handleClickOutside)
  }, [])

  const triggerClasses = [
    'flex h-12 w-full items-center rounded-lg border px-4 pr-10 font-sans text-base tracking-base transition outline-none text-left',
    disabled
      ? 'cursor-not-allowed border-neutral-300 bg-neutral-50 text-neutral-300'
      : hasError
        ? 'border-error-base bg-error-50 text-neutral-300'
        : isOpen
          ? 'border-indigo-300 bg-indigo-50 text-indigo-300'
          : value
            ? 'border-neutral-300 bg-white text-neutral-900 hover:border-indigo-600'
            : 'border-neutral-300 bg-white text-neutral-300 hover:border-indigo-600',
  ]
    .filter(Boolean)
    .join(' ')

  const iconStyles = disabled
    ? 'text-neutral-300 size-5'
    : hasError
      ? 'text-error-50 fill-error-base stroke-2 size-6.5'
      : isOpen
        ? 'text-indigo-300 size-5'
        : 'text-indigo-base size-5'

  const iconClassname = `pointer-events-none transition ${iconStyles}`

  return {
    ref,
    isOpen,
    stagedDate,
    displayedDate,
    hasError,
    displayedHelp,
    resolvedPlaceholder,
    triggerClasses,
    iconClassname,
    viewDate,
    prevMonth,
    nextMonth,
    goToDate,
    days,
    toggle,
    handleDayClick,
    handleConfirm,
    close,
  }
}
